import toast from 'react-hot-toast';
import { call, fork, takeEvery } from 'redux-saga/effects';

import { apiSlice, getQueryErrorMessage, QueryErrorMessage, QueryPayloadAction } from '@client/api';
import { i18n } from '@client/toolkit';

const I18N_NOTIFICATION_NAMESPACE = 'notification';

/**
 * This function just purely checks for errors and toast them without changing the app state
 */
function* handleRtkQueryRejected(action: QueryPayloadAction) {
  // Skip 404 errors for query endpoints (GET requests) - these are expected when navigating away from deleted resources
  const status = action.payload && 'status' in action.payload ? action.payload.status : undefined;
  const isQueryEndpoint = action.type.includes('executeQuery');
  if (status === 404 && isQueryEndpoint) {
    return;
  }

  const message: QueryErrorMessage = yield call(getQueryErrorMessage, action);

  if (message) {
    yield call(console.debug, action);

    toast.error(i18n.t(message, { ns: I18N_NOTIFICATION_NAMESPACE }), { id: 'global-error' });
  }
}

function* handleCreatingFulfilled() {
  toast.success(i18n.t('creating.fulfilled', { ns: I18N_NOTIFICATION_NAMESPACE }) ?? '');
  yield;
}

function* handleEditingFulfilled() {
  toast.success(i18n.t('editing.fulfilled', { ns: I18N_NOTIFICATION_NAMESPACE }) ?? '');
  yield;
}

function* handleDeletingFulfilled() {
  toast.success(i18n.t('deleting.fulfilled', { ns: I18N_NOTIFICATION_NAMESPACE }) ?? '');
  yield;
}

function* watchRtkQueryRejected() {
  yield takeEvery(
    [`${apiSlice.reducerPath}/executeQuery/rejected`, `${apiSlice.reducerPath}/executeMutation/rejected`],
    handleRtkQueryRejected,
  );
}

function* watchCreatingFulfilled() {
  yield takeEvery(
    [
      apiSlice.endpoints.postBuildings.matchFulfilled,
      apiSlice.endpoints.postSpaces.matchFulfilled,
      apiSlice.endpoints.postCommonAreas.matchFulfilled,
      apiSlice.endpoints.postRooms.matchFulfilled,
      apiSlice.endpoints.postParkingSpots.matchFulfilled,
      apiSlice.endpoints.postUsages.matchFulfilled,
      apiSlice.endpoints.postAgreements.matchFulfilled,
      apiSlice.endpoints.postAgreementsByAgreementIdProducts.matchFulfilled,
      apiSlice.endpoints.postAgreementsByAgreementIdParties.matchFulfilled,
      apiSlice.endpoints.postInvestments.matchFulfilled,
      apiSlice.endpoints.postProducts.matchFulfilled,
      apiSlice.endpoints.postAgreementsByAgreementIdFiles.matchFulfilled,
      apiSlice.endpoints.postSpacesBySpaceIdFiles.matchFulfilled,
    ],
    handleCreatingFulfilled,
  );
}

function* watchEditingFulfilled() {
  yield takeEvery(
    [
      apiSlice.endpoints.putBuildingsById.matchFulfilled,
      apiSlice.endpoints.putSpacesById.matchFulfilled,
      apiSlice.endpoints.putCommonAreasById.matchFulfilled,
      apiSlice.endpoints.putRoomsById.matchFulfilled,
      apiSlice.endpoints.putParkingSpotsById.matchFulfilled,
      apiSlice.endpoints.putUsagesById.matchFulfilled,
      apiSlice.endpoints.putAgreementsById.matchFulfilled,
      apiSlice.endpoints.putAgreementsByAgreementIdProductsAndPriceId.matchFulfilled,
      apiSlice.endpoints.putAgreementsByAgreementIdParties.matchFulfilled,
      apiSlice.endpoints.putAgreementsByAgreementIdSpaces.matchFulfilled,
      apiSlice.endpoints.putAgreementsByAgreementIdProducts.matchFulfilled,
      apiSlice.endpoints.putInvestmentsById.matchFulfilled,
      apiSlice.endpoints.putInvestmentsByInvestmentIdSpaces.matchFulfilled,
      apiSlice.endpoints.putOrganizationsByOrganizationIdPersons.matchFulfilled,
      apiSlice.endpoints.putResidentialUnitsByIdRooms.matchFulfilled,
      apiSlice.endpoints.putProductsById.matchFulfilled,
      apiSlice.endpoints.putPersonsById.matchFulfilled,
    ],
    handleEditingFulfilled,
  );
}

function* watchDeletingFulfilled() {
  yield takeEvery(
    [
      apiSlice.endpoints.deleteSpacesById.matchFulfilled,
      apiSlice.endpoints.deleteUsagesById.matchFulfilled,
      apiSlice.endpoints.deleteAgreementsById.matchFulfilled,
      apiSlice.endpoints.deleteAgreementsByAgreementIdProductsAndPriceId.matchFulfilled,
      apiSlice.endpoints.deleteAgreementsByAgreementIdPartiesAndLegalPersonId.matchFulfilled,
      apiSlice.endpoints.deleteInvestmentsById.matchFulfilled,
      apiSlice.endpoints.deleteOrganizationsByOrganizationIdPersonsAndPersonId.matchFulfilled,
      apiSlice.endpoints.deleteAgreementsByAgreementIdFilesAndFileId.matchFulfilled,
      apiSlice.endpoints.deleteSpacesBySpaceIdFilesAndFileId.matchFulfilled,
      apiSlice.endpoints.deleteProductsById.matchFulfilled,
    ],
    handleDeletingFulfilled,
  );
}

function* watchInvitingPersonFulfilled() {
  yield takeEvery(apiSlice.endpoints.putPersonsByIdInvite.matchFulfilled, () => {
    toast.success(i18n.t('invitingPerson.fulfilled', { ns: I18N_NOTIFICATION_NAMESPACE }) ?? '');
  });
}

function* startNotificationWatchers() {
  yield fork(watchRtkQueryRejected);
  yield fork(watchCreatingFulfilled);
  yield fork(watchEditingFulfilled);
  yield fork(watchDeletingFulfilled);
  yield fork(watchInvitingPersonFulfilled);
}

export function* notificationSaga() {
  yield fork(startNotificationWatchers);
}
